import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TYPE_NOTIFICATION } from "../../../components/reusable/NotificationBull";
import useToast from "../../../hooks/useToast";
import CandidatureService from "../../../services/CandidatureService";

const INIT_FORM_ERROR = {
  libelle_type_error: null,
  description_error: null,
  date_debut_publication_error: null,
  date_fin_publication_error: null,
  type_error: null,
  desc_error: null,
  cloture_error: null,
};

const INIT_AMI_INFO = {
  libelle_type: "",
  description: "",
  date_debut_publication: "",
  date_fin_publication: "",
  type: "interne",
  cloture: "0",
  desc: "Cons_int",
};

const OPTIONS = [
  { id: 1, label: "Consultant interne", value: "Cons_int" },
  { id: 2, label: "Consultant individuel", value: "Cons_indiv" },
  { id: 3, label: "Consultant externe", value: "Cons_ext" },
  { id: 5, label: "EMS", value: "EMS" },
];

const OptionTile = ({ active, disabled, label, value, name, onChange }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 0.5,
      borderRadius: 2,
      borderColor: active ? "primary.main" : "divider",
      backgroundColor: active ? "#EAF2FF" : "#FFFFFF",
      opacity: disabled ? 0.55 : 1,
      height: "100%",
    }}
  >
    <FormControlLabel
      value={value}
      disabled={disabled}
      control={<Radio size="small" />}
      label={<Typography fontWeight={700}>{label}</Typography>}
      name={name}
      onChange={onChange}
      sx={{ m: 0, width: "100%" }}
    />
  </Paper>
);

const InformationAMiPage = forwardRef(({ amiDetail, refresh }, ref) => {
  const [searchParams] = useSearchParams();
  const id_ami = searchParams.get("id_type");
  const navigate = useNavigate();
  const [formError, setFormError] = useState(INIT_FORM_ERROR);
  const [formLoading, setFormLoading] = useState(false);
  const [amiInfo, setAmiInfo] = useState(amiDetail || INIT_AMI_INFO);
  const { showToast, Toast } = useToast();
  const showToastRef = useRef(showToast);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  const validate = (info) => {
    const errors = {
      libelle_type_error: info.libelle_type
        ? null
        : "Le libellé du type d'AMI est requis.",
      description_error: info.description
        ? null
        : "La référence de l'AMI est requise.",
      date_debut_publication_error: info.date_debut_publication
        ? null
        : "La date de début de publication est requise.",
      date_fin_publication_error: info.date_fin_publication
        ? null
        : "La date de fin de publication est requise.",
      type_error: info.type ? null : "Le type d'AMI est requis.",
      desc_error: info.desc ? null : "L'option de l'AMI est requise.",
      cloture_error: info.cloture ? null : "Le statut de l'AMI est requis.",
    };
    setFormError(errors);
    return Object.values(errors).every((error) => error === null);
  };

  const handleFetchAMIInfo = useCallback((idAmi) => {
    if (!idAmi) return;
    setFormLoading(true);
    CandidatureService.find_one(idAmi)
      .then((res) => {
        if (res && res.data) {
          setAmiInfo(res.data);
        } else {
          showToastRef.current(
            TYPE_NOTIFICATION.ERROR,
            "Impossible de récupérer les informations de l'AMI."
          );
        }
      })
      .finally(() => {
        setFormLoading(false);
      });
  }, []);

  const handleChangeAMIInfo = (e) => {
    if (e.target.name === "type" && e.target.value === "interne") {
      setAmiInfo({
        ...amiInfo,
        [e.target.name]: e.target.value,
        desc: "Cons_int",
      });
      return;
    }

    if (e.target.name === "type" && e.target.value === "externe") {
      setAmiInfo({
        ...amiInfo,
        [e.target.name]: e.target.value,
        desc: "Cons_indiv",
      });
      return;
    }

    setAmiInfo({ ...amiInfo, [e.target.name]: e.target.value });
  };

  const save = async () => {
    if (!validate(amiInfo)) {
      showToast(
        TYPE_NOTIFICATION.ERROR,
        "Veuillez corriger les erreurs dans le formulaire avant de continuer."
      );
      return false;
    }

    const payload = {
      ...amiInfo,
      id_type: id_ami || undefined,
    };

    const result = await CandidatureService.insert(payload);

    if (result && result.success) {
      if (result.data) {
        setAmiInfo(result.data);
        searchParams.set("id_type", result.data.id_type);
        window.history.replaceState(null, "", "?" + searchParams.toString());
        navigate(`?id_type=${result.data.id_type}`, { replace: true });
      }
      showToast(
        TYPE_NOTIFICATION.SUCCESS,
        "Les informations de l'AMI ont été enregistrées avec succès."
      );
      return true;
    }

    showToast(
      TYPE_NOTIFICATION.ERROR,
      "Une erreur est survenue lors de l'enregistrement des informations de l'AMI."
    );
    return false;
  };

  useImperativeHandle(ref, () => ({
    save,
    getData: () => amiInfo,
  }));

  useEffect(() => {
    if (amiDetail) {
      setAmiInfo(amiDetail);
    }
  }, [amiDetail]);

  useEffect(() => {
    if (amiInfo) {
      validate(amiInfo);
    }
  }, [amiInfo]);

  useEffect(() => {
    handleFetchAMIInfo(id_ami);
  }, [handleFetchAMIInfo, refresh, id_ami]);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Toast />
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
        }}
      >
        <Box
          sx={{
            display: formLoading ? "flex" : "none",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          {formLoading && (
            <CircularProgress size="3rem" aria-label="Chargement" />
          )}
        </Box>

        {!formLoading && (
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "primary.main",
                    backgroundColor: "#EAF2FF",
                    flexShrink: 0,
                  }}
                >
                  <DescriptionOutlinedIcon />
                </Box>
                <Box>
                  {/* <Typography variant="overline" color="primary" fontWeight={700}>
                    Informations générales
                  </Typography> */}
                  <Typography variant="h5" fontWeight={800}>
                    Informations AMI
                  </Typography>
                  {/* <Typography variant="body2" color="text.secondary">
                    Complétez les informations de l'appel à manifestation d'intérêt.
                  </Typography> */}
                </Box>
              </Stack>
              <Chip
                label={amiInfo.cloture === "1" ? "Clôturé" : "Ouvert"}
                color={amiInfo.cloture === "1" ? "default" : "success"}
                variant={amiInfo.cloture === "1" ? "outlined" : "filled"}
                sx={{ fontWeight: 700 }}
              />
            </Stack>

            <Divider sx={{ my: 2.5 }} />

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" /* sx={{ mb: 1 }} */>
                  <InfoOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={800}>
                    Identification
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  name="description"
                  label="Référence AMI"
                  value={amiInfo.description}
                  onChange={handleChangeAMIInfo}
                  error={formError.description_error !== null}
                  helperText={formError.description_error}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <TextField
                  fullWidth
                  size="small"
                  name="libelle_type"
                  label="Libellé du type d'AMI"
                  multiline
                  minRows={3}
                  value={amiInfo.libelle_type}
                  onChange={handleChangeAMIInfo}
                  error={formError.libelle_type_error !== null}
                  helperText={formError.libelle_type_error}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <CalendarTodayOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={800}>
                    Publication
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="date_debut_publication"
                  label="Date de début"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={(amiInfo.date_debut_publication || "").split(" ")[0]}
                  onChange={handleChangeAMIInfo}
                  error={formError.date_debut_publication_error !== null}
                  helperText={formError.date_debut_publication_error}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="date_fin_publication"
                  label="Date de fin"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={(amiInfo.date_fin_publication || "").split(" ")[0]}
                  onChange={handleChangeAMIInfo}
                  error={formError.date_fin_publication_error !== null}
                  helperText={formError.date_fin_publication_error}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <TuneOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={800}>
                    Paramètres
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" fullWidth error={formError.type_error !== null}>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 700 }}>
                    Type d'AMI
                  </FormLabel>
                  <RadioGroup
                    row
                    name="type"
                    value={amiInfo.type}
                    onChange={handleChangeAMIInfo}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <OptionTile
                          active={amiInfo.type === "interne"}
                          label="Interne"
                          value="interne"
                          name="type"
                          onChange={handleChangeAMIInfo}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <OptionTile
                          active={amiInfo.type === "externe"}
                          label="Externe"
                          value="externe"
                          name="type"
                          onChange={handleChangeAMIInfo}
                        />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                  <FormHelperText>{formError.type_error}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  component="fieldset"
                  fullWidth
                  error={formError.cloture_error !== null}
                >
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 700 }}>
                    Statut de l'AMI
                  </FormLabel>
                  <RadioGroup
                    row
                    name="cloture"
                    value={amiInfo.cloture}
                    onChange={handleChangeAMIInfo}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <OptionTile
                          active={amiInfo.cloture === "0"}
                          label="Ouvert"
                          value="0"
                          name="cloture"
                          onChange={handleChangeAMIInfo}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <OptionTile
                          active={amiInfo.cloture === "1"}
                          label="Clôturé"
                          value="1"
                          name="cloture"
                          onChange={handleChangeAMIInfo}
                        />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                  <FormHelperText>{formError.cloture_error}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" fullWidth error={formError.desc_error !== null}>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 700 }}>
                    Options disponibles
                  </FormLabel>
                  <RadioGroup
                    row
                    name="desc"
                    value={amiInfo.desc}
                    onChange={handleChangeAMIInfo}
                  >
                    <Grid container spacing={1.5}>
                      {OPTIONS.map((option) => {
                        const disabled =
                          (amiInfo.type === "interne" && option.value !== "Cons_int") ||
                          (amiInfo.type === "externe" && option.value === "Cons_int");
                          if(disabled) return null;
                        return (
                          <Grid item xs={12} sm={6} md={3} key={option.id}>
                            <OptionTile
                              active={amiInfo.desc === option.value}
                              disabled={disabled}
                              // sx={{display :  disabled ? "none" : "block"}}
                              label={option.label}
                              value={option.value}
                              name="desc"
                              onChange={handleChangeAMIInfo}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </RadioGroup>
                  <FormHelperText>{formError.desc_error}</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        )}
      </Card>
    </Box>
  );
});

export default InformationAMiPage;
